import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Msg, Section, Select, panelGet, panelPost } from '../kit';
import { MultiSelect } from '../email/emailKit';
import { str } from '../ipintel/shared';
import { TagPills } from '../tags/shared';
import { CATEGORIES as TRL_CATEGORIES } from '../universities/TrlProvenance';
import { S } from './shared';

/**
 * Admin → Tech URL pages → Sections & access: two platform-wide settings kept
 * in tech_pages settings next to the section matrix (POST tech_pages/settings_save).
 *  - `tech_card_design`: "accordion" (the new expandable card in search and
 *    the tech modal) or "legacy" (the old fixed card). The app reads it from
 *    GET /api/v1/ui-config on load.
 *  - `copy_protect` (owner, 2026-10-12; on by default): tech-card text cannot
 *    be selected or copied in the app and on the public tech / university
 *    pages (Frontend utils/copyGuard.ts, also read from ui-config). Shown in
 *    the same section as the card design.
 *  - `copy_allow` (owner, 2026-10-13): who can still copy while it is on —
 *    admin accounts, user tags (manual or automatic) and e-mail addresses /
 *    @domains. The app asks GET /api/v1/ui/copy-permission per signed-in
 *    account; the read carries the tag list (`copy_allow_tags`) and how many
 *    accounts the saved list matches (`copy_allow_preview`).
 *  - `trl_copy`: the wording of the TRL block (tech_pages.trl_block) that both
 *    the platform's tech cards and the public tech pages show — a description
 *    per level 1-9, plus the title and text for a technology with no TRL. The
 *    grey note under it is the provenance category's own label (Universities →
 *    TRL provenance), shown here read-only.
 */

// ---------------------------------------------------------------------------
// Tech card design
// ---------------------------------------------------------------------------
const DESIGNS: Array<{ key: 'accordion' | 'legacy'; label: string; hint: string }> = [
  {
    key: 'accordion', label: 'Accordion (new)',
    hint: 'Search results open in place: title, university · country, TRL and relevance pills, a one-line summary; expanded, the Summary | Researchers & IP | Problem & Applications tabs with Contact University, Get IP Intel Report, share and bookmark. The tech modal (For You, Inbox, Profile) uses the same card.',
  },
  {
    key: 'legacy', label: 'Legacy (previous card)',
    hint: 'The earlier fixed-size card in a modal (Summary / IP Highlights / Publications), without share or IP Intel. Kept as a fallback.',
  },
];

export const TechCardDesign: React.FC<{ data: any }> = ({ data }) => {
  const fromData = (d: any): 'accordion' | 'legacy' => (str(d?.tech_card_design) === 'legacy' ? 'legacy' : 'accordion');
  /** Missing (a server older than the switch) reads as on, as the app does. */
  const protectFrom = (d: any): boolean => d?.copy_protect !== false;
  const [value, setValue] = useState<'accordion' | 'legacy'>(fromData(data));
  const [protect, setProtect] = useState<boolean>(protectFrom(data));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { setValue(fromData(data)); setProtect(protectFrom(data)); }, [data]);

  const save = async (next: 'accordion' | 'legacy') => {
    if (next === value || busy) return;
    const prev = value;
    setBusy(true); setMsg(null); setValue(next);
    const { data: out, error } = await panelPost(S, 'settings_save', { tech_card_design: next });
    setBusy(false);
    if (error) { setValue(prev); setMsg({ kind: 'error', text: error }); return; }
    const saved = out && typeof out === 'object' && (out as any).tech_card_design ? fromData(out) : next;
    setValue(saved);
    setMsg({ kind: 'ok', text: `Saved: ${saved === 'legacy' ? 'legacy card' : 'accordion card'}. Signed-in users get it the next time the app loads.` });
  };

  const saveProtect = async (next: boolean) => {
    if (busy) return;
    setBusy(true); setMsg(null); setProtect(next);
    const { data: out, error } = await panelPost(S, 'settings_save', { copy_protect: next });
    setBusy(false);
    if (error) { setProtect(!next); setMsg({ kind: 'error', text: error }); return; }
    const saved = out && typeof out === 'object' && 'copy_protect' in (out as any) ? protectFrom(out) : next;
    setProtect(saved);
    setMsg({ kind: 'ok', text: saved
      ? 'Saved: copy protection on. Visitors get it the next time a page loads (up to a minute).'
      : 'Saved: copy protection off. Card text can be selected and copied again from the next page load.' });
  };

  return (
    <Section title="Tech card design (platform)">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <p className="text-xs text-grayscale-1100 -mt-1">
        How technologies look inside the signed-in app. Switching back to legacy changes nothing on the public pages.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" role="radiogroup" aria-label="Tech card design">
        {DESIGNS.map(d => {
          const on = value === d.key;
          return (
            <button key={d.key} type="button" role="radio" aria-checked={on} disabled={busy}
              onClick={() => save(d.key)}
              className={`text-left rounded-lg border px-3 py-2.5 flex gap-2.5 items-start ${on
                ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50' : 'border-grayscale-300 bg-white hover:border-primary-300'}
                ${busy ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}>
              <span aria-hidden className={`mt-0.5 inline-block w-4 h-4 rounded-full border-2 shrink-0 ${on ? 'border-primary-500 bg-primary-500 shadow-[inset_0_0_0_2px_#fff]' : 'border-grayscale-500'}`} />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-grayscale-1300">{d.label}</span>
                <span className="text-[11px] text-grayscale-1100">{d.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      <label className={`flex items-start gap-2.5 border-t border-grayscale-200 pt-3 ${busy ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}>
        <input type="checkbox" className="mt-1" checked={protect} disabled={busy}
          onChange={() => saveProtect(!protect)} />
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-grayscale-1300">Stop visitors copying tech-card text</span>
          <span className="text-[11px] text-grayscale-1100">
            On: the text on tech cards (search results, the tech modal, For You, Saved, chat answers, homepage
            results) and on the public tech and university pages cannot be selected, copied or dragged. Search boxes,
            forms, links and the share / copy-link buttons work as usual, and the admin panel is never affected.
            It only deters casual copying: screenshots and browser tools still work.
          </span>
        </span>
      </label>
      <CopyAllowList data={data} protect={protect} />
      {busy && <div className="text-[11px] text-grayscale-1100">saving…</div>}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Who can still copy (tech_pages settings `copy_allow`)
// ---------------------------------------------------------------------------
type CopyAllow = { admins: boolean; tags: string[]; emails: string };
type CopyTag = { key: string; label: string; color?: string; grp?: string; auto?: boolean; n?: number | null };
type CopyPreview = {
  accounts?: number | null; admins?: number; emails?: number; tags?: number; unknown_tags?: string[]; error?: string;
};

const normAllow = (v: any): CopyAllow => ({
  admins: v?.admins !== false,
  tags: Array.isArray(v?.tags) ? v.tags.map((t: any) => String(t)) : [],
  emails: Array.isArray(v?.emails) ? v.emails.map((e: any) => String(e)).join('\n') : '',
});
/** The entries as the server stores them: trimmed, lower-cased, no repeats. */
const emailLines = (text: string): string[] =>
  Array.from(new Set(text.split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean)));
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const DOMAIN_RE = /^@[^@\s]+\.[^@\s]+$/;
const sameAllow = (a: CopyAllow, b: CopyAllow): boolean => a.admins === b.admins
  && JSON.stringify([...a.tags].sort()) === JSON.stringify([...b.tags].sort())
  && JSON.stringify(emailLines(a.emails)) === JSON.stringify(emailLines(b.emails));
const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const CopyAllowList: React.FC<{ data: any; protect: boolean }> = ({ data, protect }) => {
  const [allow, setAllow] = useState<CopyAllow>(() => normAllow(data?.copy_allow));
  const [saved, setSaved] = useState<CopyAllow>(() => normAllow(data?.copy_allow));
  const [preview, setPreview] = useState<CopyPreview | null>(data?.copy_allow_preview || null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    const n = normAllow(data?.copy_allow);
    setAllow(n); setSaved(n); setPreview(data?.copy_allow_preview || null);
  }, [data]);

  const catalogue: CopyTag[] = Array.isArray(data?.copy_allow_tags) ? data.copy_allow_tags : [];
  const known = new Set(catalogue.map(t => t.key));
  const maxEmails = Number(data?.copy_allow_limits?.emails) || 500;
  // Automatic tags first (the server's order), then the custom ones; a saved
  // tag deleted since stays visible so it can be unticked.
  const tagLabels: Record<string, string> = {
    ...Object.fromEntries(catalogue.map(t => [t.key,
      `${t.grp ? `${t.grp} › ` : ''}${t.label}${typeof t.n === 'number' ? ` (${t.n.toLocaleString()})` : ''}`])),
    ...Object.fromEntries(allow.tags.filter(k => !known.has(k)).map(k => [k, `${k} (deleted tag)`])),
  };
  const picked = allow.tags.map(k => {
    const t = catalogue.find(x => x.key === k);
    return { key: k, label: t ? t.label : `${k} (deleted)`, color: t?.color };
  });
  const lines = emailLines(allow.emails);
  const bad = lines.filter(e => !EMAIL_RE.test(e) && !DOMAIN_RE.test(e));
  const tooMany = lines.length > maxEmails;
  const dirty = !sameAllow(allow, saved);

  const save = async () => {
    if (bad.length || tooMany) return;
    setBusy(true); setMsg(null);
    const body = { admins: allow.admins, tags: allow.tags, emails: lines };
    const { data: out, error } = await panelPost(S, 'settings_save', { copy_allow: body });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const n = normAllow((out as any)?.copy_allow ?? body);
    setAllow(n); setSaved(n);
    setPreview((out as any)?.copy_allow_preview || null);
    setMsg({ kind: 'ok', text: 'Saved. It applies the next time each person loads the app (up to a minute).' });
  };

  let count: React.ReactNode = null;
  if (preview && preview.accounts !== null && preview.accounts !== undefined) {
    const parts = [
      saved.admins ? plural(preview.admins || 0, 'admin', 'admins') : '',
      preview.emails ? `${preview.emails.toLocaleString()} by address` : '',
      preview.tags ? `${preview.tags.toLocaleString()} by tag` : '',
    ].filter(Boolean);
    count = (
      <span>
        Matches <b>{plural(preview.accounts, 'account', 'accounts')}</b>
        {parts.length > 1 && <> ({parts.join(', ')}; anyone in two groups counts once)</>}
        {dirty && <span className="text-amber-700"> · saved list; save to recount</span>}
      </span>
    );
  } else if (preview) {
    count = <span className="text-red-600">Could not count the accounts{preview.error ? ` (${preview.error})` : ''}.</span>;
  }
  const ta = 'w-full min-h-[5rem] px-2.5 py-1.5 rounded-md border-[0.5px] border-primary-300 bg-white text-sm font-mono leading-snug';

  return (
    <div className={`ml-6 rounded-lg border border-grayscale-300 bg-grayscale-50 px-3 py-3 flex flex-col gap-3 ${protect ? '' : 'opacity-60'}`}>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-grayscale-1300">Who can still copy</span>
        <span className="text-[11px] text-grayscale-1100">
          These signed-in accounts can select and copy card text while the protection is on.
        </span>
      </span>
      {!protect && (
        <div className="text-[11px] text-grayscale-1200">
          Copy protection is off, so everyone can copy. This list applies once it is back on.
        </div>
      )}
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={allow.admins} disabled={busy}
          onChange={() => setAllow(a => ({ ...a, admins: !a.admins }))} />
        <span className="flex flex-col gap-0.5">
          <span className="text-sm text-grayscale-1300">Admin accounts</span>
          <span className="text-[11px] text-grayscale-1100">Everyone on Billing → Admins.</span>
        </span>
      </label>
      <div className="flex flex-col gap-1.5">
        <MultiSelect label="User tags" options={catalogue.map(t => t.key)} labels={tagLabels}
          values={allow.tags} onChange={v => setAllow(a => ({ ...a, tags: v }))} placeholder="No tags" />
        {picked.length > 0 && (
          <TagPills tags={picked} onRemove={k => setAllow(a => ({ ...a, tags: a.tags.filter(x => x !== k) }))} />
        )}
        <span className="text-[11px] text-grayscale-1100">
          Anyone with at least one of these tags, manual or automatic (User tags).
          {!catalogue.length && ' The tag list could not be loaded; reload to try again.'}
        </span>
      </div>
      <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
        E-mail addresses or @domains
        <textarea className={ta} rows={4} value={allow.emails} disabled={busy} spellCheck={false}
          placeholder={'name@company.com\n@partner.org'}
          onChange={e => setAllow(a => ({ ...a, emails: e.target.value }))} />
        <span className="text-[11px]">
          One per line. @partner.org covers every address at that exact domain, not its subdomains.
          {lines.length > 0 && ` ${lines.length.toLocaleString()} of ${maxEmails.toLocaleString()}.`}
        </span>
        <span className="text-[11px] text-amber-800">
          Sign-up does not check the inbox, so anyone can create an account with an address or @domain
          listed here. For a group you need to be sure of, give the people a user tag instead.
        </span>
      </label>
      {(bad.length > 0 || tooMany) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex flex-col gap-0.5">
          {bad.length > 0 && (
            <div>⚠ Not an e-mail address or @domain: {bad.slice(0, 5).join(', ')}{bad.length > 5 ? ` and ${bad.length - 5} more` : ''}</div>
          )}
          {tooMany && <div>⚠ At most {maxEmails.toLocaleString()} addresses and @domains.</div>}
        </div>
      )}
      {preview?.unknown_tags && preview.unknown_tags.length > 0 && (
        <div className="text-[11px] text-amber-700">
          Deleted tags still on the list match nobody: {preview.unknown_tags.join(', ')}. Untick them and save.
        </div>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="flex items-center justify-between gap-3 flex-wrap border-t border-grayscale-200 pt-2.5">
        <div className="text-xs text-grayscale-1300">{count}</div>
        <span className="flex items-center gap-2">
          {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
          <Btn small onClick={() => setAllow(saved)} disabled={busy || !dirty}>Discard changes</Btn>
          <Btn small kind="primary" onClick={save} disabled={busy || !dirty || bad.length > 0 || tooMany}>
            {busy ? 'Saving…' : 'Save'}
          </Btn>
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TRL messaging
// ---------------------------------------------------------------------------
type TrlCopy = { levels: Record<string, string>; not_found_title: string; not_found_description: string };

const LEVELS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
/** The contract defaults (tech_pages DEFAULT_SETTINGS.trl_copy); the server's own copy wins when it sends one. */
export const DEFAULT_TRL_COPY: TrlCopy = {
  levels: {
    1: 'Basic principles have been observed and reported, the earliest stage of scientific research.',
    2: 'The technology concept and its practical application have been formulated, still speculative with no experimental proof.',
    3: 'Analytical and laboratory studies have validated the key function or characteristic, indicating an experimental proof of concept.',
    4: 'Component subsystems have been integrated and tested in the laboratory, indicating validation in a controlled environment.',
    5: 'Integrated components have been tested in a simulated or relevant environment, indicating validation beyond the laboratory.',
    6: 'A representative prototype has been demonstrated in a relevant environment, close to but short of the intended operating conditions.',
    7: 'A system prototype has been demonstrated in an operational environment.',
    8: 'The actual system has been completed and qualified through test and demonstration.',
    9: 'The actual system has been proven through successful operation in its intended environment.',
  },
  not_found_title: 'TRL not found',
  not_found_description: 'The university or technology owner has not provided a TRL.',
};

const MAX_LEN = 400;

const normCopy = (v: any, base: TrlCopy): TrlCopy => ({
  levels: Object.fromEntries(LEVELS.map(l => [l, str(v?.levels?.[l]) || base.levels[l] || ''])),
  not_found_title: str(v?.not_found_title) || base.not_found_title,
  not_found_description: str(v?.not_found_description) || base.not_found_description,
});

/** Which description each provenance category shows. */
const CATEGORY_RULE: Record<string, string> = {
  disclosed_confirmed: 'Level text below',
  disclosed_mismatch: 'Level text below (the disclosed TRL is shown)',
  disclosed_no_evidence: 'Level text below',
  inferred: 'The technology’s own evidence statement when it reads as a sentence (30+ characters once machine prefixes are removed), else the level text',
  not_disclosed_no_evidence: '“Not found” title and text below; pill “TRL —”',
};

const TrlPreview: React.FC<{ title: string; description: string; note: string }> = ({ title, description, note }) => (
  <div className="rounded-lg border border-grayscale-300 bg-white px-4 py-3" style={{ borderLeft: '4px solid #886A44' }}>
    <div className="text-base font-bold text-grayscale-1300">{title}</div>
    <div className="text-sm text-grayscale-1300 mt-0.5">{description || <span className="text-grayscale-1100 italic">(empty)</span>}</div>
    <div className="text-[11px] text-grayscale-1100 mt-1.5">{note}</div>
  </div>
);

export const TrlMessaging: React.FC<{ data: any }> = ({ data }) => {
  const defaults = useMemo(() => normCopy(data?.defaults?.trl_copy, DEFAULT_TRL_COPY), [data]);
  const [copy, setCopy] = useState<TrlCopy>(() => normCopy(data?.trl_copy, defaults));
  const [base, setBase] = useState(() => JSON.stringify(normCopy(data?.trl_copy, defaults)));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [cat, setCat] = useState<string>('disclosed_confirmed');
  const [lvl, setLvl] = useState('6');

  useEffect(() => {
    const n = normCopy(data?.trl_copy, defaults);
    setCopy(n); setBase(JSON.stringify(n));
  }, [data, defaults]);

  const dirty = JSON.stringify(copy) !== base;
  const savedCopy = useMemo<TrlCopy | null>(() => { try { return JSON.parse(base); } catch { return null; } }, [base]);
  const setLevel = (l: string, v: string) => setCopy(c => ({ ...c, levels: { ...c.levels, [l]: v } }));
  const problems = useMemo(() => {
    const p: string[] = [];
    LEVELS.forEach(l => { if (!copy.levels[l]?.trim()) p.push(`TRL ${l} has no text.`); });
    if (!copy.not_found_title.trim()) p.push('The “not found” title is empty.');
    if (!copy.not_found_description.trim()) p.push('The “not found” text is empty.');
    [...LEVELS.map(l => copy.levels[l] || ''), copy.not_found_description].forEach((t, i) => {
      if (t.length > MAX_LEN) p.push(`${i < 9 ? `TRL ${i + 1}` : '“Not found” text'} is over ${MAX_LEN} characters.`);
    });
    return p;
  }, [copy]);

  const save = async () => {
    if (problems.length) return;
    setBusy(true); setMsg(null);
    const body: TrlCopy = {
      levels: Object.fromEntries(LEVELS.map(l => [l, copy.levels[l].trim()])),
      not_found_title: copy.not_found_title.trim(),
      not_found_description: copy.not_found_description.trim(),
    };
    const { data: out, error } = await panelPost(S, 'settings_save', { trl_copy: body });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const n = normCopy((out as any)?.trl_copy ?? body, defaults);
    setCopy(n); setBase(JSON.stringify(n));
    setMsg({ kind: 'ok', text: 'Saved. Tech cards and public tech pages pick it up within about 5 minutes (server cache).' });
  };

  const catLabel = (k: string) => TRL_CATEGORIES.find(c => c.key === k)?.label || k;
  const previewNotFound = cat === 'not_disclosed_no_evidence';
  const ta = 'w-full min-h-[3.25rem] px-2.5 py-1.5 rounded-md border-[0.5px] border-primary-300 bg-white text-sm leading-snug';

  return (
    <Section title="TRL messaging"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          {dirty && <span className="text-[11px] text-amber-700">unsaved changes</span>}
          <Btn small onClick={() => setCopy(defaults)} disabled={busy} title="Put the default wording back (still needs Save)">Defaults</Btn>
          <Btn small onClick={() => savedCopy && setCopy(savedCopy)} disabled={busy || !dirty}>Discard changes</Btn>
          <Btn small kind="primary" onClick={save} disabled={busy || !dirty || problems.length > 0}>{busy ? 'Saving…' : 'Save'}</Btn>
        </div>
      }>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <p className="text-xs text-grayscale-1100 -mt-1">
        The TRL block on every tech card and public tech page: a bold title (“TRL 6”, or the “not found” title), one
        line of description, and a small grey note saying where the TRL came from. The note is the provenance
        category’s label and is not edited here.
      </p>

      <div className="rounded-lg border border-grayscale-300 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-grayscale-200 text-grayscale-1200 text-xs">
              <th className="text-left px-3 py-2 font-grotesk">Provenance category</th>
              <th className="text-left px-3 py-2 font-grotesk">Grey note (read-only)</th>
              <th className="text-left px-3 py-2 font-grotesk">Description used</th>
            </tr>
          </thead>
          <tbody>
            {TRL_CATEGORIES.map(c => (
              <tr key={c.key} className="border-t border-grayscale-200 align-top">
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className="inline-flex w-5 h-5 rounded-full items-center justify-center text-[10px] font-bold"
                      style={{ backgroundColor: c.color, color: c.ink }}>{c.no}</span>
                    <span className="font-mono text-[11px] text-grayscale-1100">{c.key}</span>
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-grayscale-1300">{c.label}</td>
                <td className="px-3 py-2 text-[11px] text-grayscale-1100">{CATEGORY_RULE[c.key] || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-4">
        <div className="flex flex-col gap-2">
          {LEVELS.map(l => {
            const changed = !!savedCopy && savedCopy.levels[l] !== copy.levels[l];
            const len = (copy.levels[l] || '').length;
            return (
              <label key={l} className={`flex gap-3 items-start rounded-md px-2 py-1 ${changed ? 'bg-amber-50' : ''}`}>
                <span className="w-12 shrink-0 pt-1.5 text-xs font-bold text-grayscale-1300">TRL {l}</span>
                <span className="flex-1 flex flex-col gap-0.5">
                  <textarea className={ta} value={copy.levels[l]} rows={2} aria-label={`TRL ${l} description`}
                    onChange={e => setLevel(l, e.target.value)} />
                  <span className={`text-[10px] self-end ${len > MAX_LEN ? 'text-red-600' : 'text-grayscale-1100'}`}>{len}/{MAX_LEN}</span>
                </span>
              </label>
            );
          })}
          <div className="border-t border-grayscale-200 pt-2 flex flex-col gap-2">
            <label className={`flex gap-3 items-center rounded-md px-2 py-1 ${savedCopy && savedCopy.not_found_title !== copy.not_found_title ? 'bg-amber-50' : ''}`}>
              <span className="w-28 shrink-0 text-xs font-bold text-grayscale-1300">“Not found” title</span>
              <input className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-64" value={copy.not_found_title}
                maxLength={60} onChange={e => setCopy(c => ({ ...c, not_found_title: e.target.value }))} />
              <span className="text-[11px] text-grayscale-1100">the pill reads “TRL —”</span>
            </label>
            <label className={`flex gap-3 items-start rounded-md px-2 py-1 ${savedCopy && savedCopy.not_found_description !== copy.not_found_description ? 'bg-amber-50' : ''}`}>
              <span className="w-28 shrink-0 pt-1.5 text-xs font-bold text-grayscale-1300">“Not found” text</span>
              <textarea className={ta} rows={2} value={copy.not_found_description}
                onChange={e => setCopy(c => ({ ...c, not_found_description: e.target.value }))} />
            </label>
          </div>
          {problems.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex flex-col gap-0.5">
              {problems.map(p => <div key={p}>⚠ {p}</div>)}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-xs font-bold text-grayscale-1200">Preview</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={cat} onChange={setCat} options={TRL_CATEGORIES.map(c => c.key)}
              labels={Object.fromEntries(TRL_CATEGORIES.map(c => [c.key, `${c.no}. ${c.label.length > 34 ? `${c.label.slice(0, 33)}…` : c.label}`]))} />
            {!previewNotFound && (
              <Select value={lvl} onChange={setLvl} options={LEVELS} labels={Object.fromEntries(LEVELS.map(l => [l, `TRL ${l}`]))} />
            )}
          </div>
          <TrlPreview
            title={previewNotFound ? (copy.not_found_title || 'TRL not found') : `TRL ${lvl}`}
            description={previewNotFound ? copy.not_found_description : copy.levels[lvl]}
            note={catLabel(cat)} />
          {cat === 'inferred' && (
            <div className="text-[11px] text-grayscale-1100">
              Inferred TRLs usually show their own evidence statement instead of this level text.
            </div>
          )}
          <div className="text-[11px] text-grayscale-1100">
            Pill on the collapsed card: <b>{previewNotFound ? 'TRL —' : `TRL ${lvl}`}</b>
          </div>
        </div>
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Similar technologies (tech_pages settings `similar_tech`; the saved top 3
// per tech — see Backend/similar_tech.py)
// ---------------------------------------------------------------------------
type SimMethod = 'keyword' | 'embedding';
interface SimilarCfg { method: SimMethod; scope: 'any' | 'same_university'; max_same_university: number }
const normSimilar = (v: any): SimilarCfg => ({
  method: v?.method === 'embedding' ? 'embedding' : 'keyword',
  scope: v?.scope === 'same_university' ? 'same_university' : 'any',
  max_same_university: typeof v?.max_same_university === 'number' ? v.max_same_university : 2,
});
const METHOD_TEXT: Record<SimMethod, { label: string; hint: string }> = {
  keyword: {
    label: 'Keyword match (no AI)',
    hint: 'Shared keywords, keyword phrases, title words and sub-categories; rarer shared terms count more. Rebuild here in about 30 seconds.',
  },
  embedding: {
    label: 'Meaning match (AI embeddings)',
    hint: 'The same embeddings the search uses: finds related work even when it is worded differently. Rebuilt with scripts/build_tech_similar.py --method embedding.',
  },
};

export const SimilarTechSettings: React.FC<{ data: any }> = ({ data }) => {
  const [cfg, setCfg] = useState<SimilarCfg>(() => normSimilar(data?.similar_tech));
  const [base, setBase] = useState(() => JSON.stringify(normSimilar(data?.similar_tech)));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [builds, setBuilds] = useState<any>(data?.similar_builds || {});
  const [job, setJob] = useState<any>(data?.similar_job || null);
  useEffect(() => {
    const n = normSimilar(data?.similar_tech);
    setCfg(n); setBase(JSON.stringify(n));
    setBuilds(data?.similar_builds || {}); setJob(data?.similar_job || null);
  }, [data]);
  const saved = useMemo(() => { try { return JSON.parse(base) as SimilarCfg; } catch { return cfg; } }, [base, cfg]);
  const dirty = JSON.stringify(cfg) !== base;

  // While a rebuild runs, ask for its status every 3 s.
  useEffect(() => {
    if (!job?.running) return;
    const t = window.setInterval(async () => {
      const { data: st } = await panelGet(S, 'similar_status');
      if (st) { setJob((st as any).job); setBuilds((st as any).builds || {}); }
    }, 3000);
    return () => window.clearInterval(t);
  }, [job?.running]);

  const save = async () => {
    setBusy(true); setMsg(null);
    const { data: out, error } = await panelPost(S, 'settings_save', { similar_tech: cfg });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const n = normSimilar((out as any)?.similar_tech ?? cfg);
    const capChanged = n.max_same_university !== saved.max_same_university;
    setCfg(n); setBase(JSON.stringify(n));
    setMsg({ kind: 'ok', text: capChanged
      ? 'Saved. The same-university limit applies when the lists are rebuilt — press Rebuild.'
      : 'Saved. Cards and pages pick it up within five minutes (their cache).' });
  };

  const rebuild = async () => {
    setMsg(null);
    const { data: out, error } = await panelPost(S, 'similar_rebuild', { method: 'keyword' });
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setJob((out as any)?.job || null);
  };

  const buildLine = (m: SimMethod) => {
    const b = builds?.[m];
    if (!b) return <span className="text-red-600">not built — technologies show the old rule (newest at the same university)</span>;
    const missing = Number(b.total || 0) - Number(b.covered || 0);
    return (
      <>
        built {new Date(b.built_at).toLocaleString()} · {Number(b.covered || 0).toLocaleString()} of {Number(b.total || 0).toLocaleString()} technologies
        {missing > 0 && <span className="text-red-600"> · {missing.toLocaleString()} newer ones use the old rule until a rebuild</span>}
        {b.stats?.cap !== undefined && <> · built with at most {b.stats.cap} of 3 from the same university</>}
      </>
    );
  };

  return (
    <Section title="Similar technologies">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <p className="text-xs text-grayscale-1100 -mt-1 max-w-3xl">
        The three closest technologies are worked out for every technology at once and saved. The same technology
        listed twice is skipped. A technology added after the last build shows the old rule until the next one.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" role="radiogroup" aria-label="How similar is measured">
        {(Object.keys(METHOD_TEXT) as SimMethod[]).map(m => {
          const on = cfg.method === m;
          return (
            <button key={m} type="button" role="radio" aria-checked={on} onClick={() => setCfg(c => ({ ...c, method: m }))}
              className={`text-left rounded-lg border px-3 py-2.5 flex gap-2.5 items-start ${on
                ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50' : 'border-grayscale-300 bg-white hover:border-primary-300'}`}>
              <span aria-hidden className={`mt-0.5 inline-block w-4 h-4 rounded-full border-2 shrink-0 ${on ? 'border-primary-500 bg-primary-500 shadow-[inset_0_0_0_2px_#fff]' : 'border-grayscale-500'}`} />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-grayscale-1300">{METHOD_TEXT[m].label}</span>
                <span className="text-[11px] text-grayscale-1100">{METHOD_TEXT[m].hint}</span>
                <span className="text-[11px] text-grayscale-1100">{buildLine(m)}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-end gap-4 text-sm">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Where from
          <Select value={cfg.scope} onChange={v => setCfg(c => ({ ...c, scope: v as SimilarCfg['scope'] }))}
            options={['any', 'same_university']}
            labels={{ any: 'Any university', same_university: 'Same university only' }} />
        </label>
        {cfg.scope === 'any' && (
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            At most from its own university (on rebuild)
            <select value={cfg.max_same_university}
              onChange={e => setCfg(c => ({ ...c, max_same_university: Number(e.target.value) }))}
              className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
              {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n} of 3</option>)}
            </select>
          </label>
        )}
        <Btn kind="primary" onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn onClick={rebuild} disabled={!!job?.running || dirty}
          title={dirty ? 'Save first' : 'Recompute the keyword lists for every technology (about 30 seconds)'}>
          {job?.running ? 'Rebuilding keyword lists…' : 'Rebuild keyword lists'}
        </Btn>
      </div>
      {job?.running && <div className="text-[11px] text-grayscale-1100">{(job.log || []).slice(-1)[0] || 'starting…'}</div>}
      {job && !job.running && job.error && <Msg kind="error">Rebuild failed: {job.error}</Msg>}
      {job && !job.running && job.result && (
        <div className="text-[11px] text-green-700">
          Rebuilt {Number(job.result.techs).toLocaleString()} technologies in {job.result.seconds}s.
        </div>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// University pages (tech_pages settings `university_pages`)
// ---------------------------------------------------------------------------
export const UniversityPagesSettings: React.FC<{ data: any }> = ({ data }) => {
  const norm = (v: any) => ({ enabled: !!v?.enabled, listing_links: !!v?.listing_links });
  const [cfg, setCfg] = useState(() => norm(data?.university_pages));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { setCfg(norm(data?.university_pages)); }, [data]);

  const save = async (next: { enabled: boolean; listing_links: boolean }) => {
    const prev = cfg;
    setCfg(next); setBusy(true); setMsg(null);
    const { data: out, error } = await panelPost(S, 'settings_save', { university_pages: next });
    setBusy(false);
    if (error) { setCfg(prev); setMsg({ kind: 'error', text: error }); return; }
    setCfg(norm((out as any)?.university_pages ?? next));
    setMsg({ kind: 'ok', text: 'Saved. Pages and links follow within about a minute (their caches).' });
  };

  const row = (on: boolean, disabled: boolean, onToggle: () => void, title: string, hint: string) => (
    <label className={`flex items-start gap-2.5 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" className="mt-1" checked={on} disabled={disabled || busy} onChange={onToggle} />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-grayscale-1300">{title}</span>
        <span className="text-[11px] text-grayscale-1100">{hint}</span>
      </span>
    </label>
  );

  return (
    <Section title="University pages">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="flex flex-col gap-3">
        {row(cfg.enabled, false, () => save({ ...cfg, enabled: !cfg.enabled }),
          'University pages (/universities/<university>)',
          'Off: no link to a university page anywhere — tech pages, platform cards, the /universities listing — the university sitemap is empty, and anyone opening one lands on /universities (a temporary redirect).')}
        {row(cfg.listing_links && cfg.enabled, !cfg.enabled, () => save({ ...cfg, listing_links: !cfg.listing_links }),
          '“View technologies →” on the /universities listing',
          cfg.enabled ? 'Each university card on /universities links to its page.' : 'Needs university pages on.')}
      </div>
    </Section>
  );
};
